/**
 * Локальное хранилище документов бесплатных инструментов.
 *
 * Всё, что человек набирает в редакторе, остаётся в его браузере: файлы
 * не уходят на сервер, потому что для JSON и Markdown сервер не нужен
 * вовсе, а чужие данные лучше не хранить без причины. Отсюда IndexedDB,
 * а не localStorage: документы бывают на сотни килобайт, а localStorage
 * синхронный и общий лимит у него порядка пяти мегабайт.
 *
 * Модуль намеренно без зависимостей: обёртки вроде idb добавили бы пакет
 * ради полутора промисов.
 *
 * Все функции безопасны на сервере: при отсутствии `indexedDB` они
 * возвращают пустой результат, а не падают. Это важно, потому что
 * страницы редакторов пререндерятся.
 */

import { randomId } from '@/lib/random-id';

const DB_NAME = 'datahunter-tools';
const DB_VERSION = 1;
const STORE = 'documents';

/** Инструмент, которым создан документ. */
export type DocumentKind = 'json' | 'markdown';

/** Документ в списке недавних. */
export interface StoredDocument {
  /** Идентификатор, он же ключ записи. */
  id: string;
  kind: DocumentKind;
  /** Заголовок для списка: имя файла или первая строка. */
  title: string;
  content: string;
  /** Время последнего сохранения, мс. */
  updatedAt: number;
}

/** Максимум записей на инструмент: список недавних, а не архив. */
const KEEP_PER_KIND = 20;

function available(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        // Список строится по инструменту и по времени: без индекса пришлось бы
        // читать всё хранилище целиком на каждой загрузке страницы.
        store.createIndex('kind_updatedAt', ['kind', 'updatedAt']);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB недоступна'));
    };
  });
}

/**
 * Обёртка над одной транзакцией.
 *
 * Тип результата задаётся вызывающим: методы IDBObjectStore объявлены
 * возвращающими `IDBRequest<any>`, потому что хранилище не типизировано.
 * Приведение сосредоточено здесь, чтобы `any` не расползался по коду.
 */
/**
 * Хранилище с типами наших документов.
 *
 * Стандартные объявления IndexedDB отдают `any`: база схемы не знает и
 * знать не может. Сужение описано здесь один раз, а не на каждом вызове,
 * поэтому дальше по коду типы настоящие, и линтер не спотыкается об `any`.
 */
interface TypedStore {
  getAll: () => IDBRequest<StoredDocument[]>;
  get: (key: string) => IDBRequest<StoredDocument | undefined>;
  put: (value: StoredDocument) => IDBRequest<IDBValidKey>;
  delete: (key: string) => IDBRequest<undefined>;
}

function typed(store: IDBObjectStore): TypedStore {
  return store;
}

function tx<T>(mode: IDBTransactionMode, run: (store: TypedStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(typed(transaction.objectStore(STORE)));

        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          reject(request.error ?? new Error('Ошибка запроса к IndexedDB'));
        };
        transaction.oncomplete = () => {
          db.close();
        };
      }),
  );
}

/** Новый идентификатор документа. */
export function newDocumentId(): string {
  return randomId();
}

/**
 * Недавние документы инструмента, новые сверху.
 * Возвращает пустой список, если хранилище недоступно: приватный режим
 * некоторых браузеров запрещает IndexedDB, и редактор должен работать
 * и без истории.
 */
export async function listDocuments(kind: DocumentKind): Promise<StoredDocument[]> {
  if (!available()) return [];

  try {
    const all = await tx<StoredDocument[]>('readonly', (store) => store.getAll());
    return all
      .filter((doc) => doc.kind === kind)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, KEEP_PER_KIND);
  } catch {
    return [];
  }
}

/** Один документ по идентификатору. */
export async function getDocument(id: string): Promise<StoredDocument | null> {
  if (!available()) return null;

  try {
    const doc = await tx<StoredDocument | undefined>('readonly', (store) => store.get(id));
    return doc ?? null;
  } catch {
    return null;
  }
}

/** Сохраняет документ и подчищает хвост истории. */
export async function saveDocument(doc: StoredDocument): Promise<void> {
  if (!available()) return;

  try {
    await tx('readwrite', (store) => store.put(doc));

    // Хранилище пользователя не должно расти бесконечно от нашей истории:
    // всё, что не поместилось в список недавних, удаляем.
    const all = await tx<StoredDocument[]>('readonly', (store) => store.getAll());
    const extra = all
      .filter((d) => d.kind === doc.kind)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(KEEP_PER_KIND);

    for (const old of extra) await deleteDocument(old.id);
  } catch {
    // Молча: невозможность сохранить черновик не повод ломать редактор.
  }
}

/** Удаляет документ из истории. */
export async function deleteDocument(id: string): Promise<void> {
  if (!available()) return;

  try {
    await tx('readwrite', (store) => store.delete(id));
  } catch {
    // См. выше: история вторична по отношению к самому редактированию.
  }
}
